import type { PiniaPluginContext, StoreGeneric } from "pinia";

/**
 * Plugin Pinia : dote chaque store d'un `$reset()` fiable (y compris les setup
 * stores, pour lesquels `$reset` lève une erreur par défaut) et tient un
 * registre permettant de tout réinitialiser en une fois.
 *
 * Motivation : à la déconnexion, seule l'identité (`authStore`) était vidée. Les
 * stores de domaine (lacs, réservations, tarification, campagnes…) gardaient les
 * données du tenant précédent, car logout→login est une navigation SPA sans
 * rechargement. Sur un poste partagé, l'utilisateur suivant voyait donc les
 * données de l'organisation précédente jusqu'au re-fetch page par page.
 */

const storeRegistry = new Set<StoreGeneric>();

/**
 * Clone profond de l'état d'un store.
 *
 * `structuredClone` ne convient PAS ici : l'état Pinia expose des `Ref` et des
 * proxies réactifs, qu'il refuse avec `DataCloneError`. La première version de
 * ce plugin s'appuyait dessus et retombait sur un `return` silencieux — donc
 * AUCUN store n'était enregistré et `resetAllStores()` ne faisait rien.
 *
 * On parcourt donc l'état via le proxy `$state` (qui déréférence les `Ref`) en
 * traitant explicitement les types réellement présents dans les stores.
 */
function deepClone<T>(value: T): T {
  if (value === null || typeof value !== "object") return value;
  if (value instanceof Date) return new Date(value.getTime()) as unknown as T;
  if (value instanceof Set) {
    return new Set(Array.from(value, (item) => deepClone(item))) as unknown as T;
  }
  if (value instanceof Map) {
    return new Map(
      Array.from(value, ([key, item]) => [key, deepClone(item)]),
    ) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => deepClone(item)) as unknown as T;
  }
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    out[key] = deepClone(item);
  }
  return out as T;
}

export function piniaResetPlugin({ store }: PiniaPluginContext) {
  const initialState = deepClone({ ...store.$state }) as Record<string, unknown>;

  store.$reset = () => {
    const fresh = deepClone(initialState);
    store.$patch((state) => {
      Object.assign(state, fresh);
    });
  };

  storeRegistry.add(store);
}

/**
 * Stores qui ne portent AUCUNE donnée de tenant et ne doivent jamais être
 * réinitialisés avec le reste :
 * - `notification` : file de toasts visibles. La purger effaçait le message
 *   « session expirée » au moment même où on le venait de poser, ainsi que tout
 *   toast concurrent.
 * - `consent` : consentement RGPD, persisté en localStorage. Son état initial
 *   est capturé à la création du store ; le réinitialiser après une acceptation
 *   faisait réapparaître la bannière alors que le choix était déjà enregistré.
 */
const NEVER_RESET = new Set(["notification", "consent"]);

/**
 * Réinitialise tous les stores enregistrés à leur état initial.
 * @param except identifiants de stores à préserver (ex. `auth` en cours de logout).
 */
export function resetAllStores(except: string[] = []): void {
  const skip = new Set(except);
  storeRegistry.forEach((store) => {
    if (skip.has(store.$id) || NEVER_RESET.has(store.$id)) return;
    try {
      store.$reset();
    } catch {
      // Un store dont le reset échoue ne doit pas bloquer les autres.
    }
  });
}
